import React, { useEffect, useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import ROUTES from '../Routes/constants';
import ReducerConstants from '../../_shared/constants/reducers';
import AuthService from '../../services/AuthService';
import UserService from '../../services/UserService';
import MixpanelService from '../../services/MixpanelService';
import MixpanelConfig from '../../_shared/constants/mixpanelConfig';
import { COOKIES } from '../../_shared/constants/cookies';
import { createCookie } from '../../_shared/utils/cookies';
import { updateShowChat } from '../../redux/slices/ui/layout';
import useFeatureFlags from '../../_shared/hooks/useFeatureFlags';
import FEATURE_FLAGS from '../../_shared/constants/featureFlags';

import {
  StyledDealerProfile,
  StyledProfile,
  StyledNameContainer,
  StyledName,
  StyledIcon,
  StyledButton,
  UpArrow
} from './Styles';

const DealerProfile = React.forwardRef((props, ref) => {
  const { username, isDealerProfileOpen, setIsDealerProfileOpen } = props;
  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );
  const dispatch = useDispatch();
  const userService = UserService.get();
  const mixpanel = MixpanelService.get();
  const isOMPUser = useFeatureFlags(FEATURE_FLAGS.IS_OMP_USER);

  const menuItems = useMemo(
    () => [
      {
        id: 'accounting',
        label: <FormattedMessage id="tr5.nav.menu.accounting" />,
        icon: 'TR5-Calculator',
        href: `${ROUTES.ACCOUNTING.replace(':page', '')}${
          user?.isBillingModelEnabled ? 'account-overview' : 'list'
        }`,
        show: user?.isCompanyAdmin || user?.isAccountant,
        newTab: false
      },
      {
        id: 'payments_on_hold',
        label: <FormattedMessage id="list.header.paymentsOnHold" />,
        icon: 'TR5-Payments',
        href: ROUTES.PAYMENTS_ON_HOLD,
        show: user?.isCanSell || user?.isAccountant,
        newTab: false
      },
      {
        id: 'reports',
        label: <FormattedMessage id="tr5.nav.menu.reports" />,
        icon: 'TR5-Computer',
        href: `${ROUTES.REPORTS.replace(':context', '')}${
          user?.isCanSell ? 'sell' : 'buy'
        }`,
        show: userService.canViewReports(user),
        newTab: false
      },
      {
        id: 'settings',
        label: <FormattedMessage id="tr5.nav.menu.settings" />,
        icon: 'TR5-Settings_gear',
        href: ROUTES.SETTINGS,
        show: true,
        newTab: false
      },
      {
        id: 'chat',
        label: <FormattedMessage id="tr5.nav.menu.chat" />,
        icon: 'TR5-Headset',
        show: true,
        newTab: false
      },
      {
        id: 'support',
        label: <FormattedMessage id="tr5.nav.menu.support" />,
        icon: 'support-icon',
        href: ROUTES.SUPPORT,
        show: true,
        newTab: true
      },
      {
        id: 'logout',
        label: <FormattedMessage id="tr5.nav.menu.logout" />,
        icon: 'TR5-Logout',
        href: ROUTES.TR4_LOGIN,
        show: true,
        newTab: false
      },
      {
        id: 'switch_user',
        label: <FormattedMessage id="tr5.nav.menu.switchUser" />,
        icon: 'TR5-Switch_User',
        href: ROUTES.SWITCH_USER,
        show: true,
        newTab: false
      }
    ],
    [userService, user]
  );

  useEffect(() => {
    const handleClickOutside = event => {
      if (!ref.current.contains(event.target)) {
        setIsDealerProfileOpen(false);
      }
    };

    if (isDealerProfileOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [ref, setIsDealerProfileOpen, isDealerProfileOpen]);

  const handleLogout = async item => {
    const logout = await AuthService.logout();

    if (logout?.ok) {
      AuthService.handlePostLogout();
      mixpanel.track(MixpanelConfig.names.dealerProfileMenu, {
        dealer_option: 'logout'
      });
    }
    if (isOMPUser) {
      window.location.replace(ROUTES.OMP_LOGOUT);
    } else {
      window.location.replace(item?.href);
    }
  };

  const displayChatWidget = () => {
    createCookie(COOKIES.IS_CHAT_ENABLED, true);
    dispatch(updateShowChat(true));
    setIsDealerProfileOpen(false);
    // eslint-disable-next-line
    setTimeout(function() {
      const button = document.getElementById('chatFiveIcon');
      if (button) {
        // Trigger button click to force open the Chat widget
        button.click();
      }
    }, 300);
    mixpanel.track(MixpanelConfig.names.initiateChat, {});
  };

  const handleClickMenuItem = item => {
    mixpanel.track(MixpanelConfig.names.dealerProfileMenu, {
      dealer_option: item?.id?.replace(/_/g, ' ')
    });

    if (item?.href === ROUTES.PAYMENTS_ON_HOLD) {
      mixpanel.track(MixpanelConfig.names.openPaymentsOnHoldPage, {
        source: 'Main Menu'
      });
    }

    setIsDealerProfileOpen(false);
  };

  return (
    <StyledDealerProfile
      data-testid="nav-profile-menu"
      ref={ref}
      isDealerProfileOpen={isDealerProfileOpen}
    >
      <StyledProfile>
        <StyledIcon icon="TR5-Profile" />
        <StyledNameContainer>
          <StyledName className="notranslate">{username}</StyledName>
          <Link
            to={ROUTES.ACCOUNT_PROFILE}
            data-testid="nav-profile-menu-dealer_info_screen"
            onClick={() =>
              mixpanel.track(MixpanelConfig.names.dealerProfileMenu, {
                dealer_option: 'edit profile'
              })
            }
          >
            <FormattedMessage id="tr5.nav.menu.editProfile" />
          </Link>
        </StyledNameContainer>
      </StyledProfile>
      {user &&
        !user?.isSuspended &&
        Object.keys(user).length > 0 &&
        menuItems.map(item => {
          // Do not show 'Switch User' if there are no users to switch to
          if (item?.id === 'logout') {
            return (
              <li key={`menu-${item.id}`}>
                <StyledButton
                  testId={`nav-profile-menu-${item.id}`}
                  onClick={() => handleLogout(item.href)}
                >
                  <StyledIcon icon={item.icon} />
                  {item.label}
                </StyledButton>
              </li>
            );
          }
          if (item?.id === 'chat') {
            return (
              <li key={`menu-${item.id}`}>
                <StyledButton
                  testId={`nav-profile-menu-${item.id}`}
                  onClick={() => {
                    displayChatWidget();
                  }}
                >
                  <StyledIcon icon={item.icon} />
                  {item.label}
                </StyledButton>
              </li>
            );
          }

          return (
            item?.id !== 'logout' &&
            item?.id !== 'chat' &&
            item?.show && (
              <li key={`menu-${item.id}`}>
                <Link
                  to={item?.href}
                  data-testid={`nav-profile-menu-${item.id}`}
                  target={item.newTab ? '_blank' : '_self'}
                  onClick={() => handleClickMenuItem(item)}
                >
                  <StyledIcon icon={item.icon} />
                  {item.label}
                </Link>
              </li>
            )
          );
        })}

      <UpArrow />
    </StyledDealerProfile>
  );
});

export default DealerProfile;
